import { AcroNode } from "./types";
import { renderEditText } from "./renderEditText";
import { renderCheckbox } from "./renderCheckbox";
import { renderLabel } from "./renderLabel";
import { renderRowsGrid } from "./renderRowsGrid";
import { groupWithOptions } from "./groupWithOptions";
import { escapeHtml } from "./escapeHtml";
import { renderGroupAllowMultipleTable } from "./renderGroupAllowMultipleTable";
import { renderRadio } from "./renderRadio";

export function renderNode(node: AcroNode, parentId?: string): string {
  const t = (node.type || '').toLowerCase();
  switch (t) {
    case 'edittext':
      return renderEditText(node);
    case 'checkbox':
      return renderCheckbox(node);
    case 'radio':
      // Use parentId as the radio group name if provided
      return renderRadio(node, parentId);
    case 'label':
      return renderLabel(node);
    case 'group': {
      // Special rule: allow_multiple group as table
      if (node.allow_multiple) {
        return renderGroupAllowMultipleTable(node);
      }
      const title = node.title ?? node.sub_title ?? null;
      const safeTitle = title ? escapeHtml(String(title)) : escapeHtml('Group');
      const children = node.children ?? [];
      // Hide group title if any child has the same title (case-insensitive, trimmed)
      let showGroupTitle = !!title;
      if (title) {
        const normTitle = String(title).trim().toLowerCase();
        if (normTitle === 'group') {
          showGroupTitle = false;
        }
      }
      if (showGroupTitle && title && children.length > 0) {
        const normTitle = String(title).trim().toLowerCase();
        for (const c of children) {
          if (c.title && String(c.title).trim().toLowerCase() === normTitle) {
            showGroupTitle = false;
            break;
          }
        }
      }
      const maybeTable = groupWithOptions(node);
      if (maybeTable) return maybeTable;
      if ((node.options ?? []).length > 0) {
        const fieldId = node.field_id ? escapeHtml(String(node.field_id)) : "";
        const fieldIdAttr = fieldId ? ` data-field-id="${fieldId}"` : "";
        const optsHtml = (node.options ?? []).map((opt, i) => `
          <label class="flex items-center gap-2 py-1">
            <input type="checkbox" data-id="${escapeHtml(String(node.id ?? ""))}__opt_${i}"${fieldIdAttr} class="form-checkbox" />
            <span class="text-sm">${escapeHtml(String(opt))}</span>
          </label>
        `).join("");
        const desc = node.description ? `<div class="text-xs text-gray-500 mb-2">${escapeHtml(String(node.description))}</div>` : '';
        return `
          <div class="flex flex-col gap-4 mb-3">
${showGroupTitle ? `            <div class="text-sm font-medium text-gray-700">${safeTitle}</div>` : ''}
            ${desc}
            <div class="divide-y divide-gray-100">${optsHtml || `<div class="text-xs text-gray-400">No options</div>`}</div>
          </div>
        `;
      }
      const rowsHtml = renderRowsGrid(children, node.id);
      const desc = node.description ? `<div class="section-group-description text-xs text-gray-500">${escapeHtml(String(node.description))}</div>` : '';
      return `
        <div class="section-group flex flex-auto flex-wrap gap-2 py-3 px-4 border border-gray-300 rounded-lg">
${showGroupTitle ? `          <div class="section-group-title basis-full text-sm font-medium text-gray-700">${safeTitle}</div>` : ''}
          ${desc}
          <div class="section-group-content flex flex-wrap gap-4">
            ${rowsHtml}
          </div>
        </div>
      `;
    }
    default: {
      const title = node.title ? `<div class="text-sm text-gray-700">${escapeHtml(String(node.title))}</div>` : '';
      const desc = node.description ? `<div class="text-xs text-gray-500">${escapeHtml(String(node.description))}</div>` : '';
      if ((node.children ?? []).length > 0) {
        return `
          <section class="flex flex-col gap-4 mb-3">
            ${title}${desc}
            ${renderRowsGrid(node.children)}
          </section>
        `;
      }
      return `<div class="py-2">${title}${desc}</div>`;
    }
  }
}
